import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useExtensionInstalled from '../../hooks/useExtensionInstalled';

const CHROME_STORE_URL =
    'https://chromewebstore.google.com/detail/flowdino-gestion-multi-pl/iealfioknccembacfpjgodhoehdmggak';

/**
 * Bandeau global : sans l'extension Chrome, l'essentiel de FlowDino (publication,
 * import, messages) ne fonctionne pas. Il reste donc affiché sur toutes les pages tant
 * que l'extension est absente.
 *
 * La fermeture ne vaut que pour la session en cours, volontairement : masquer
 * durablement l'installation laisserait un compte inutilisable sans explication.
 */
const ExtensionBanner = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { isInstalled, recheck } = useExtensionInstalled();
    const [dismissed, setDismissed] = useState(false);
    const [isChecking, setIsChecking] = useState(false);

    const handleRecheck = async () => {
        setIsChecking(true);
        await recheck();
        setIsChecking(false);
    };

    // `isInstalled === null` : détection en cours, on n'affiche rien pour éviter un
    // bandeau qui apparaît puis disparaît à chaque chargement.
    if (!user || isInstalled !== false || dismissed) {
        return null;
    }

    return (
        <div className="bg-amber-500 border-b border-amber-600 text-white">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3 sm:px-6 lg:px-8">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                </svg>

                <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-semibold">{t('onboarding.extensionBanner.title')}</p>
                    <p className="text-sm text-amber-50">{t('onboarding.extensionBanner.description')}</p>
                </div>

                <div className="flex items-center gap-2">
                    <a
                        href={CHROME_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-white text-amber-700 text-sm font-medium rounded-md hover:bg-amber-50"
                    >
                        {t('onboarding.extensionBanner.install')}
                    </a>

                    <button
                        type="button"
                        onClick={handleRecheck}
                        disabled={isChecking}
                        className="px-3 py-2 text-sm font-medium text-white border border-white/60 rounded-md hover:bg-amber-600 disabled:opacity-60"
                    >
                        {isChecking
                            ? t('onboarding.extensionBanner.checking')
                            : t('onboarding.extensionBanner.alreadyInstalled')}
                    </button>

                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        aria-label={t('onboarding.close')}
                        className="p-2 text-white/80 hover:text-white"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExtensionBanner;
